// views/quizbuilder.tsx — the Quiz page builder.
//
// Three columns, Typeform-shaped:
//   PAGES (left)     every screen the shopper walks, in order — intro A/B
//                    variants, the interleaved flow (info cards + questions),
//                    then results. Add / reorder / delete here.
//   CANVAS (center)  a preview laid out like the real storefront page. Every
//                    text you can change is an input sitting exactly where that
//                    text lands on the front end. Click it, type, done.
//   INSPECTOR (right) the things that aren't copy — scoring weights, image slot,
//                    colors, multi-select. Scoring is per-option × per-area.
//
// It is NOT a live render of the storefront (that's Shopify's job) — it's a
// faithful-enough mock that you can tell at a glance which box is which.
//
// IMAGES: the files live in the Shopify theme. The admin picks a SLOT; the theme
// resolves the slot to an asset. So image tiles here show a placeholder + the
// slot name and send you to the theme editor. That keeps images on Shopify's CDN
// and means a missing image is a blank tile, never a broken quiz.

import React from "react";
import { OSWALD, FIGTREE, useT, useIsMobile } from "../theme";
import { Icon, Card, Badge, Btn } from "../atoms";
import { AREAS, AREA_LABEL, IMAGE_SLOTS } from "./quizcontent";

type Sel =
  | { kind: "intro"; idx: number }
  | { kind: "step"; idx: number }
  | { kind: "results" }
  | { kind: "products" }
  | { kind: "ui" };

const sameSel = (a: Sel, b: Sel) =>
  a.kind === b.kind && (a as any).idx === (b as any).idx;

// ============================================================================
// shell
// ============================================================================
export function QuizBuilder({ doc, touch }: { doc: any; touch: (fn: (d: any) => void) => void }) {
  const t = useT();
  const isMobile = useIsMobile();
  const [sel, setSel] = React.useState<Sel>({ kind: "intro", idx: 0 });

  // Keep the selection in range when pages are added/removed.
  const flow = doc.flow || [];
  React.useEffect(() => {
    if (sel.kind === "step" && sel.idx >= flow.length) setSel({ kind: "intro", idx: 0 });
    if (sel.kind === "intro" && sel.idx >= (doc.intros || []).length) setSel({ kind: "intro", idx: 0 });
  }, [flow.length, (doc.intros || []).length]);

  // Desktop: page-list rail | canvas | settings inspector. On narrow/mobile the
  // three fixed columns (268 + 296 of chrome) would push the page off-screen, so
  // they stack into one column — rail, then canvas, then settings.
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "268px minmax(0,1fr) 296px", gap: 14, alignItems: "start" }}>
      <PagesRail doc={doc} touch={touch} sel={sel} setSel={setSel} stack={isMobile} />
      <Card pad={0} style={{ overflow: "hidden", background: t.surface2, minWidth: 0 }}>
        <Canvas doc={doc} touch={touch} sel={sel} />
      </Card>
      <Inspector doc={doc} touch={touch} sel={sel} stack={isMobile} />
    </div>
  );
}

// ============================================================================
// PAGES rail
// ============================================================================
function PagesRail({ doc, touch, sel, setSel, stack }: any) {
  const t = useT();
  const intros = doc.intros || [];
  const flow = doc.flow || [];

  function addQuestion() {
    touch((d: any) => {
      const n = (d.questions || []).length + 1;
      const id = uniqueId("q" + n, (d.questions || []).map((q: any) => q.id));
      (d.questions = d.questions || []).push({
        // ART KEY, not a theme path. The theme resolves keys via ART[]; a raw
        // path falls through to the origin check, resolves same-origin and
        // renders a broken image. STARTER was fixed for this; these two
        // add-new paths were missed, so every question added in the builder
        // shipped a 404 illustration.
        id, theme: "general", image: "illBindRemove", caption: "",
        title: "", sub: "", multi: false, options: [],
      });
      (d.flow = d.flow || []).push({ type: "q", qid: id });
    });
    setSel({ kind: "step", idx: flow.length });
  }
  function addInfo() {
    touch((d: any) => {
      const key = uniqueId("info", Object.keys(d.info || {}));
      (d.info = d.info || {})[key] = {
        bg: "#3c3291", ink: "#ffffff", illo: "illOrganSupport", // art key, not a path — see addQuestion
        eyebrow: "", title: "", body: "", cta: "Continue",
      };
      (d.flow = d.flow || []).push({ type: "info", key });
    });
    setSel({ kind: "step", idx: flow.length });
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= flow.length) return;
    touch((d: any) => { const f = d.flow; [f[i], f[j]] = [f[j], f[i]]; });
    setSel({ kind: "step", idx: j });
  }
  function removeStep(i: number) {
    touch((d: any) => {
      const step = d.flow[i];
      d.flow.splice(i, 1);
      // Only drop the underlying content if nothing else in the flow points at it.
      const stillUsed = d.flow.some((s: any) => s.type === step.type && (s.qid ?? s.key) === (step.qid ?? step.key));
      if (stillUsed) return;
      if (step.type === "q") d.questions = (d.questions || []).filter((q: any) => q.id !== step.qid);
      else delete (d.info || {})[step.key];
    });
    setSel({ kind: "intro", idx: 0 });
  }

  let qn = 0;
  return (
    <Card pad={12} style={{ position: stack ? "static" : "sticky", top: 12, minWidth: 0, overflow: "hidden" }}>
      <RailHeading>Intro · A/B</RailHeading>
      <div style={{ display: "grid", gap: 4, marginBottom: 12 }}>
        {intros.map((iv: any, i: number) => (
          <RailItem key={i} active={sameSel(sel, { kind: "intro", idx: i })} onClick={() => setSel({ kind: "intro", idx: i })}
            chip={String.fromCharCode(65 + i)} chipTone="purple" label={iv.title || "Untitled variant"} />
        ))}
      </div>

      <RailHeading
        right={
          <div style={{ display: "flex", gap: 4 }}>
            <MiniBtn onClick={addInfo} title="Add an info break">+ Info</MiniBtn>
            <MiniBtn onClick={addQuestion} title="Add a question">+ Q</MiniBtn>
          </div>
        }
      >
        Pages
      </RailHeading>
      <div style={{ display: "grid", gap: 4, marginBottom: 12 }}>
        {flow.length === 0 && <RailEmpty>No pages yet.</RailEmpty>}
        {flow.map((step: any, i: number) => {
          const isQ = step.type === "q";
          if (isQ) qn++;
          const q = isQ ? (doc.questions || []).find((x: any) => x.id === step.qid) : null;
          const info = !isQ ? (doc.info || {})[step.key] : null;
          const missing = isQ ? !q : !info;
          return (
            <RailItem
              key={i}
              active={sameSel(sel, { kind: "step", idx: i })}
              onClick={() => setSel({ kind: "step", idx: i })}
              chip={isQ ? String(qn) : "i"}
              chipTone={isQ ? "blue" : "amber"}
              label={missing ? `⚠ missing ${isQ ? step.qid : step.key}` : (isQ ? q.title : info.title) || "Untitled"}
              onUp={i > 0 ? () => move(i, -1) : undefined}
              onDown={i < flow.length - 1 ? () => move(i, 1) : undefined}
              onRemove={() => removeStep(i)}
            />
          );
        })}
      </div>

      <RailHeading>Ending</RailHeading>
      <div style={{ display: "grid", gap: 4 }}>
        <RailItem active={sel.kind === "results"} onClick={() => setSel({ kind: "results" })} chip="R" chipTone="green" label="Results page" />
        <RailItem active={sel.kind === "products"} onClick={() => setSel({ kind: "products" })} chip="P" chipTone="green" label="Products" />
        <RailItem active={sel.kind === "ui"} onClick={() => setSel({ kind: "ui" })} chip="T" chipTone="neutral" label="Buttons & email copy" />
      </div>
    </Card>
  );
}

function RailHeading({ children, right }: any) {
  const t = useT();
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, minHeight: 22 }}>
      <span style={{ fontFamily: FIGTREE, fontSize: 10.5, fontWeight: 700, color: t.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>{children}</span>
      {right}
    </div>
  );
}
function RailEmpty({ children }: any) {
  const t = useT();
  return <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint, padding: "6px 2px" }}>{children}</div>;
}
function MiniBtn({ children, onClick, title }: any) {
  const t = useT();
  return (
    <button onClick={onClick} title={title} style={{
      fontFamily: FIGTREE, fontSize: 11, fontWeight: 700, color: t.accent, background: "none",
      border: "1px solid " + t.border, borderRadius: 6, padding: "2px 6px", cursor: "pointer",
    }}>{children}</button>
  );
}

const CHIP_TONES: Record<string, [string, string]> = {
  purple: ["#e4dffb", "#3c3291"], blue: ["#dbeafe", "#1e40af"],
  amber: ["#fef3c7", "#92400e"], green: ["#dcfce7", "#166534"], neutral: ["#e8e8e8", "#444444"],
};

function RailItem({ active, onClick, chip, chipTone = "neutral", label, onUp, onDown, onRemove }: any) {
  const t = useT();
  const [hover, setHover] = React.useState(false);
  const [bg, fg] = CHIP_TONES[chipTone] || CHIP_TONES.neutral;
  return (
    <div
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onClick={onClick}
      style={{
        display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 8px", borderRadius: 9, cursor: "pointer",
        minWidth: 0, background: active ? t.surface2 : hover ? t.surface2 : "transparent",
        border: "1px solid " + (active ? t.border : "transparent"),
      }}
    >
      <span style={{
        flex: "0 0 auto", width: 20, height: 20, borderRadius: 6, background: bg, color: fg,
        fontFamily: FIGTREE, fontSize: 11, fontWeight: 800, display: "grid", placeItems: "center",
      }}>{chip}</span>
      {/* Wraps to two lines, then ellipses. Page titles are full sentences — truncating
          them to one line made half the rail unreadable. */}
      <span style={{
        flex: 1, minWidth: 0, fontFamily: FIGTREE, fontSize: 12.5, lineHeight: 1.35,
        color: active ? t.fg : t.fg2, overflow: "hidden", overflowWrap: "anywhere",
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
      } as React.CSSProperties}>{label}</span>
      {hover && (
        <span style={{ display: "flex", gap: 1, flex: "0 0 auto" }} onClick={(e) => e.stopPropagation()}>
          {onUp && <IconBtn name="chevron" rotate={-90} onClick={onUp} title="Move up" />}
          {onDown && <IconBtn name="chevron" rotate={90} onClick={onDown} title="Move down" />}
          {onRemove && <IconBtn name="x" onClick={onRemove} title="Delete page" />}
        </span>
      )}
    </div>
  );
}
function IconBtn({ name, onClick, title, rotate }: any) {
  const t = useT();
  return (
    <button onClick={onClick} title={title} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "inline-flex" }}>
      <Icon name={name} size={13} color={t.faint} style={rotate ? { transform: `rotate(${rotate}deg)` } : undefined} />
    </button>
  );
}

// ============================================================================
// inline-editable text — looks like the front end, behaves like an input
// ============================================================================
function T({ value, onChange, placeholder, style, area, ink }: any) {
  const t = useT();
  const [focus, setFocus] = React.useState(false);
  const common: React.CSSProperties = {
    width: "100%", boxSizing: "border-box", background: focus ? "rgba(255,255,255,.14)" : "transparent",
    border: "1px dashed " + (focus ? "rgba(255,255,255,.55)" : "rgba(128,128,128,.35)"),
    borderRadius: 6, padding: "2px 6px", color: ink || "inherit", outline: "none",
    font: "inherit", letterSpacing: "inherit", textAlign: "inherit" as any, resize: "vertical",
    ...style,
  };
  const props = {
    value: value ?? "", placeholder, onFocus: () => setFocus(true), onBlur: () => setFocus(false),
    onChange: (e: any) => onChange(e.target.value), style: common,
  };
  return area ? <textarea rows={2} {...props} /> : <input {...props} />;
}

// A stand-in for artwork that lives in the Shopify theme.
function ImageTile({ slot, ink, height = 150, note = true }: any) {
  const label = IMAGE_SLOTS.find((s) => s.id === slot)?.label || slot || "No image";
  return (
    <div style={{
      height, borderRadius: 14, display: "grid", placeItems: "center", gap: 6, padding: 12, textAlign: "center",
      background: "rgba(255,255,255,.16)", border: "1px dashed rgba(255,255,255,.5)", color: ink || "#fff",
    }}>
      <Icon name="image" size={22} color={ink || "#fff"} />
      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, fontWeight: 700, opacity: 0.95 }}>{label}</div>
      {note && <div style={{ fontFamily: FIGTREE, fontSize: 10.5, opacity: 0.75, lineHeight: 1.35, maxWidth: 220 }}>
        Artwork is managed in Shopify → Theme editor
      </div>}
    </div>
  );
}

// ============================================================================
// CANVAS
// ============================================================================
function Canvas({ doc, touch, sel }: any) {
  if (sel.kind === "intro") return <IntroCanvas doc={doc} touch={touch} idx={sel.idx} />;
  if (sel.kind === "results") return <ResultsCanvas doc={doc} touch={touch} />;
  if (sel.kind === "products") return <ProductsCanvas doc={doc} touch={touch} />;
  if (sel.kind === "ui") return <UiCanvas doc={doc} touch={touch} />;

  const step = (doc.flow || [])[sel.idx];
  if (!step) return <CanvasPad><Missing>Select a page.</Missing></CanvasPad>;
  if (step.type === "info") return <InfoCanvas doc={doc} touch={touch} k={step.key} />;
  return <QuestionCanvas doc={doc} touch={touch} qid={step.qid} />;
}

function CanvasPad({ children, bg }: any) {
  return <div style={{ padding: 22, minHeight: 460, minWidth: 0, background: bg || undefined }}>{children}</div>;
}
function Missing({ children }: any) {
  const t = useT();
  return <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, padding: 20 }}>{children}</div>;
}
function ScreenLabel({ children, ink }: any) {
  return (
    <div style={{
      fontFamily: FIGTREE, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.09em",
      opacity: 0.6, marginBottom: 12, color: ink || "inherit",
    }}>{children}</div>
  );
}

// ---- intro ----
function IntroCanvas({ doc, touch, idx }: any) {
  const iv = (doc.intros || [])[idx];
  if (!iv) return <CanvasPad><Missing>No such intro variant.</Missing></CanvasPad>;
  const set = (k: string) => (v: any) => touch((d: any) => { d.intros[idx][k] = v; });
  const ink = "#ffffff";
  return (
    <CanvasPad bg="#3c3291">
      <ScreenLabel ink={ink}>Intro · variant {String.fromCharCode(65 + idx)}</ScreenLabel>
      <div style={{ maxWidth: 560, margin: "0 auto", textAlign: "center", color: ink, display: "grid", gap: 10 }}>
        <T value={iv.eyebrow} onChange={set("eyebrow")} placeholder="Eyebrow" ink={ink}
          style={{ fontFamily: OSWALD, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.14em", textAlign: "center" }} />
        <T value={iv.title} onChange={set("title")} placeholder="Big title" ink={ink} area
          style={{ fontFamily: OSWALD, fontSize: 30, fontWeight: 700, lineHeight: 1.1, textAlign: "center" }} />
        <T value={iv.sub} onChange={set("sub")} placeholder="Subhead" ink={ink} area
          style={{ fontFamily: FIGTREE, fontSize: 14, lineHeight: 1.5, textAlign: "center", opacity: 0.92 }} />
        <div style={{ display: "grid", placeItems: "center", margin: "6px 0" }}>
          <div style={{ background: "#f47f49", borderRadius: 999, padding: "10px 26px", minWidth: 190 }}>
            <T value={iv.cta} onChange={set("cta")} placeholder="Button text" ink="#fff"
              style={{ fontFamily: OSWALD, fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center", border: "1px dashed rgba(255,255,255,.45)" }} />
          </div>
        </div>
        <T value={iv.note} onChange={set("note")} placeholder="Footnote" ink={ink}
          style={{ fontFamily: FIGTREE, fontSize: 12, textAlign: "center", opacity: 0.8 }} />
        <ImageTile slot={iv.image} ink={ink} height={130} />
      </div>
    </CanvasPad>
  );
}

// ---- info break ----
function InfoCanvas({ doc, touch, k }: any) {
  const info = (doc.info || {})[k];
  if (!info) return <CanvasPad><Missing>Info card “{k}” is referenced by the flow but doesn’t exist.</Missing></CanvasPad>;
  const set = (key: string) => (v: any) => touch((d: any) => { d.info[k][key] = v; });
  const ink = info.ink || "#fff";
  return (
    <CanvasPad bg={info.bg}>
      <ScreenLabel ink={ink}>Info break · full screen</ScreenLabel>
      <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "center", color: ink, display: "grid", gap: 12 }}>
        <ImageTile slot={info.illo} ink={ink} height={140} />
        <T value={info.eyebrow} onChange={set("eyebrow")} placeholder="Eyebrow" ink={ink}
          style={{ fontFamily: OSWALD, fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.14em", textAlign: "center" }} />
        <T value={info.title} onChange={set("title")} placeholder="Title" ink={ink} area
          style={{ fontFamily: OSWALD, fontSize: 26, fontWeight: 700, lineHeight: 1.15, textAlign: "center" }} />
        <T value={info.body} onChange={set("body")} placeholder="Body copy" ink={ink} area
          style={{ fontFamily: FIGTREE, fontSize: 14, lineHeight: 1.55, textAlign: "center", opacity: 0.94 }} />
        <div style={{ display: "grid", placeItems: "center", marginTop: 4 }}>
          <div style={{ background: "rgba(255,255,255,.22)", borderRadius: 999, padding: "9px 24px", minWidth: 170 }}>
            <T value={info.cta} onChange={set("cta")} placeholder="Button text" ink={ink}
              style={{ fontFamily: OSWALD, fontSize: 13.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center" }} />
          </div>
        </div>
      </div>
    </CanvasPad>
  );
}

// ---- question (desktop split layout, as shipped) ----
function QuestionCanvas({ doc, touch, qid }: any) {
  const t = useT();
  const qi = (doc.questions || []).findIndex((q: any) => q.id === qid);
  const q = (doc.questions || [])[qi];
  if (!q) return <CanvasPad><Missing>Question “{qid}” is referenced by the flow but doesn’t exist.</Missing></CanvasPad>;

  const theme = (doc.themes || {})[q.theme || "general"] || { bg: "#3c3291", ink: "#fff" };
  const set = (k: string) => (v: any) => touch((d: any) => { d.questions[qi][k] = v; });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,240px) minmax(0,1fr)", minHeight: 460 }}>
      {/* left themed panel — art + caption */}
      <div style={{ background: theme.bg, color: theme.ink, padding: 18, display: "grid", gap: 12, alignContent: "start" }}>
        <ScreenLabel ink={theme.ink}>Left panel</ScreenLabel>
        {/* Eyebrow falls back to the THEME's label, which every question of that
            theme shares. Typing here overrides it for this question only — the
            placeholder shows what ships if you leave it blank. */}
        <T value={q.eyebrow} onChange={set("eyebrow")} placeholder={theme.label || "Eyebrow"} ink={theme.ink}
          style={{ fontFamily: OSWALD, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", opacity: q.eyebrow ? 1 : 0.75 }} />
        <ImageTile slot={q.image} ink={theme.ink} height={130} />
        <T value={q.caption} onChange={set("caption")} placeholder="One-line caption" ink={theme.ink} area
          style={{ fontFamily: FIGTREE, fontSize: 12.5, lineHeight: 1.5 }} />
      </div>

      {/* right — the question itself */}
      <div style={{ padding: 22, display: "grid", gap: 12, alignContent: "start" }}>
        <ScreenLabel>Question</ScreenLabel>
        <T value={q.title} onChange={set("title")} placeholder="Question title" area
          style={{ fontFamily: OSWALD, fontSize: 24, fontWeight: 700, lineHeight: 1.2, color: t.fg }} />
        <T value={q.sub} onChange={set("sub")} placeholder="Subhead (e.g. “Pick the closest fit.”)"
          style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }} />

        <div style={{ display: "grid", gap: 7, marginTop: 4 }}>
          {(q.options || []).map((o: any, oi: number) => (
            <div key={oi} style={{
              display: "flex", alignItems: "center", gap: 9, padding: "9px 11px", borderRadius: 12,
              border: "1px solid " + t.border, background: t.surface,
            }}>
              <span style={{
                flex: "0 0 auto", width: 21, height: 21, borderRadius: 6, background: t.surface2,
                fontFamily: FIGTREE, fontSize: 11, fontWeight: 700, color: t.muted, display: "grid", placeItems: "center",
              }}>{q.multi ? "☐" : oi + 1}</span>
              <T value={o.label} onChange={(v: any) => touch((d: any) => { d.questions[qi].options[oi].label = v; })}
                placeholder="Answer label" style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.fg }} />
              <ScoreSummary o={o} />
              {o.exclusive && <Badge tone="neutral">exclusive</Badge>}
              <IconBtn name="x" title="Remove answer" onClick={() => touch((d: any) => d.questions[qi].options.splice(oi, 1))} />
            </div>
          ))}
          <div>
            <Btn size="sm" variant="outline" onClick={() => touch((d: any) => {
              const opts = (d.questions[qi].options = d.questions[qi].options || []);
              opts.push({ id: uniqueId("opt" + (opts.length + 1), opts.map((x: any) => x.id)), label: "", score: {}, reasons: {} });
            })}>+ Answer</Btn>
          </div>
        </div>

        {q.multi && (
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            <div style={{ display: "inline-block", background: "#f47f49", borderRadius: 999, padding: "8px 22px" }}>
              <span style={{ fontFamily: OSWALD, fontSize: 13, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {doc.ui?.continueLabel || "Continue"}
              </span>
            </div>
            <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 5 }}>
              Multi-select shows a Continue button. Edit its label under “Buttons & email copy”.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact "what does this answer score" pill row, shown inline on each answer.
function ScoreSummary({ o }: any) {
  const t = useT();
  const parts = AREAS.filter((a) => Number(o.score?.[a]) > 0).map((a) => `${AREA_LABEL[a]} +${o.score[a]}`);
  if (o.kit) parts.push(`Kit +${o.kit}`);
  if (!parts.length) return <span style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.faint, flex: "0 0 auto" }}>no score</span>;
  return (
    <span style={{ display: "flex", gap: 4, flex: "0 0 auto" }}>
      {parts.map((p) => (
        <span key={p} style={{
          fontFamily: FIGTREE, fontSize: 10, fontWeight: 700, color: t.accent,
          background: t.surface2, border: "1px solid " + t.border, borderRadius: 999, padding: "2px 7px", whiteSpace: "nowrap",
        }}>{p}</span>
      ))}
    </span>
  );
}

// ---- results ----
function ResultsCanvas({ doc, touch }: any) {
  const t = useT();
  const ui = doc.ui || {};
  const set = (k: string) => (v: any) => touch((d: any) => { (d.ui = d.ui || {})[k] = v; });
  const demo = [{ a: "liver", v: 88 }, { a: "parasite", v: 60 }, { a: "metal", v: 38 }, { a: "kidney", v: 25 }];
  return (
    <CanvasPad>
      <ScreenLabel>Results</ScreenLabel>
      <div style={{ maxWidth: 560, margin: "0 auto", display: "grid", gap: 14 }}>
        <div style={{ display: "grid", placeItems: "center" }}>
          <div style={{ background: "#f47f49", borderRadius: 999, padding: "9px 24px", minWidth: 220 }}>
            <T value={ui.emailCta} onChange={set("emailCta")} placeholder="Email CTA" ink="#fff"
              style={{ fontFamily: OSWALD, fontSize: 13.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center" }} />
          </div>
        </div>

        <div style={{ border: "1px solid " + t.border, borderRadius: 14, padding: 14, background: t.surface }}>
          <T value={ui.resultsMatchLabel} onChange={set("resultsMatchLabel")} placeholder="How strongly each detox matched your answers" ink={t.muted}
            style={{ fontFamily: OSWALD, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 9 }} />
          <div style={{ display: "grid", gap: 7 }}>
            {demo.map((d, i) => (
              <div key={d.a} style={{ display: "grid", gridTemplateColumns: "84px 1fr 34px", gap: 8, alignItems: "center" }}>
                <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2 }}>{AREA_LABEL[d.a]}</span>
                <span style={{ height: 9, borderRadius: 999, background: t.surface2, overflow: "hidden" }}>
                  <span style={{ display: "block", height: "100%", width: d.v + "%", background: i === 0 ? "#f47f49" : (doc.themes?.[d.a]?.bg || t.accent) }} />
                </span>
                <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.muted, textAlign: "right" }}>{d.v}%</span>
              </div>
            ))}
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 9 }}>
            Bar values are computed from answer scores — {doc.settings?.scoreCap ?? 8} points reads as 100%.
            The numbers aren’t editable; the headings around them are.
          </div>

          <div style={{ borderTop: "1px solid " + t.border, marginTop: 11, paddingTop: 11 }}>
            <T value={ui.scoreHowLabel} onChange={set("scoreHowLabel")} placeholder="How the score works" ink={t.muted}
              style={{ fontFamily: OSWALD, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }} />
            <T value={ui.scoreHowText} onChange={set("scoreHowText")} placeholder="Every answer adds points to the detox areas it signals…" ink={t.fg2} area
              style={{ fontFamily: FIGTREE, fontSize: 11.5, lineHeight: 1.55 }} />
          </div>
        </div>

        <div style={{ border: "1px solid " + t.border, borderRadius: 14, padding: 14, background: "#efeafc" }}>
          <T value={ui.profileEyebrow} onChange={set("profileEyebrow")} placeholder="Your detox profile" ink="#3c3291"
            style={{ fontFamily: OSWALD, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em" }} />
          <div style={{ fontFamily: OSWALD, fontSize: 19, fontWeight: 700, color: "#2d2470", textTransform: "uppercase", margin: "6px 0 10px" }}>
            Your Recommended Stack
          </div>
          <T value={ui.profileLabel} onChange={set("profileLabel")} placeholder="What we’re seeing" ink="#5b4fb0"
            style={{ fontFamily: OSWALD, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }} />
          <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: "#2d2470", marginTop: 6, lineHeight: 1.5, fontStyle: "italic" }}>
            The big title and the paragraph under this heading change with each shopper’s answers, but the
            wording is yours — there’s one version of each per outcome under “Buttons &amp; email copy”.
            The reason chips are still edited per answer in the inspector.
          </div>
        </div>

        <div style={{ border: "1px solid " + t.border, borderRadius: 14, padding: 14, background: t.surface }}>
          <div style={{ fontFamily: FIGTREE, fontSize: 10.5, fontWeight: 800, color: t.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            Recommended products
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2, lineHeight: 1.5 }}>
            Ranked automatically from the scores. Names, prices and any live discount come straight from
            Shopify — see the Products page.
          </div>
        </div>

        <T value={ui.disclaimer} onChange={set("disclaimer")} placeholder="Disclaimer" area
          style={{ fontFamily: FIGTREE, fontSize: 11, color: t.muted, lineHeight: 1.5 }} />
      </div>
    </CanvasPad>
  );
}

// ---- products ----
function ProductsCanvas({ doc, touch }: any) {
  const t = useT();
  const products = doc.products || {};
  return (
    <CanvasPad>
      <ScreenLabel>Products</ScreenLabel>
      <div style={{
        border: "1px solid " + t.border, borderRadius: 12, padding: 12, background: t.surface,
        fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.55, marginBottom: 14,
      }}>
        <b style={{ color: t.fg }}>Names and prices are read-only on purpose.</b> The storefront renders them
        live from the Shopify product, so a running sale shows up on the quiz automatically and nothing here
        can drift from checkout. The values below are only the fallback used if Shopify is unreachable.
        Change a price in Shopify, not here.
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {Object.entries(products).map(([k, p]: any) => (
          <div key={k} style={{
            display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 10, alignItems: "center",
            border: "1px solid " + t.border, borderRadius: 11, padding: "9px 11px", background: t.surface,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: FIGTREE, fontSize: 13, fontWeight: 700, color: t.fg }}>{p.name}</div>
              <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>/{p.handle}</div>
            </div>
            <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{p.days}</span>
            <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>${Number(p.price).toFixed(2)}</span>
            <Badge tone="neutral">fallback</Badge>
          </div>
        ))}
      </div>
    </CanvasPad>
  );
}

// One copy string as a collapsed single-line row that slides open to a full,
// multi-line textarea. Replaces the old always-single-line input that ran off
// screen for long strings (results paragraphs, email intros). Collapsed shows a
// truncated preview; open reveals the entire value for reading + editing in
// place. One row open at a time is handled by the parent (openField state).
// Each copy line is its own self-contained card. Label sits ABOVE the value (not
// beside it) so there's no fixed-width label column forcing the row wider than the
// builder's middle track — that overflow was clipping both the preview and the
// expanded editor off the right edge. Stacked + minWidth:0 everywhere means the
// content always wraps inside its card, and the textarea shows the full text.
function CopyField({ label, value, onChange, open, onToggle }: any) {
  const t = useT();
  const [hover, setHover] = React.useState(false);
  const v = value ?? "";
  return (
    <div style={{
      minWidth: 0, borderRadius: 10, overflow: "hidden",
      border: "1px solid " + (open ? t.borderStrong : t.border),
      background: t.surface,
    }}>
      <div
        onClick={onToggle}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        style={{
          display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", cursor: "pointer",
          transition: "background .15s", background: hover && !open ? t.surface2 : "transparent",
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: t.muted }}>{label}</span>
          {!open && (
            <span style={{
              display: "block", marginTop: 3, fontFamily: FIGTREE, fontSize: 13.5, lineHeight: 1.4,
              color: v ? t.fg2 : t.faint,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{v || <span style={{ fontStyle: "italic" }}>empty</span>}</span>
          )}
        </span>
        <span style={{ flex: "0 0 auto", display: "inline-flex", marginTop: 1, color: open ? t.accent : t.faint, transition: "transform .25s ease", transform: open ? "rotate(180deg)" : "none" }}>
          <Icon name="chevron" size={15} color="currentColor" style={{ transform: "rotate(90deg)" }} />
        </span>
      </div>
      <div style={{ overflow: "hidden", transition: "max-height .3s ease", maxHeight: open ? 340 : 0 }}>
        <div style={{ padding: "0 14px 14px" }}>
          <textarea
            value={v} onChange={(e) => onChange(e.target.value)} rows={4}
            style={{
              display: "block", width: "100%", boxSizing: "border-box", fontFamily: FIGTREE, fontSize: 13.5, lineHeight: 1.55,
              color: t.fg, background: t.surface2, border: "1px solid " + t.borderStrong, borderRadius: 8,
              padding: "10px 12px", outline: "none", resize: "vertical", minHeight: 92,
              whiteSpace: "pre-wrap", overflowWrap: "anywhere",
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ---- buttons & email copy ----
function UiCanvas({ doc, touch }: any) {
  const t = useT();
  const ui = doc.ui || {};
  const set = (k: string) => (v: any) => touch((d: any) => { (d.ui = d.ui || {})[k] = v; });
  // Which copy row is expanded — one open at a time (keyed "sectionIdx-fieldKey").
  const [openField, setOpenField] = React.useState<string | null>(null);
  // Keys mirror the theme's UI object exactly — see quizcontent.ts.
  const groups: [string, [string, string][]][] = [
    ["Navigation", [["backLabel", "Back"], ["continueLabel", "Continue"], ["lastStepLabel", "Last step button"], ["progressWord", "Progress word"]]],
    ["Exit sheet", [["exitTitle", "Title"], ["exitBody", "Body"], ["exitStay", "Keep going button"], ["exitRestart", "Start over button"], ["exitLeave", "Leave button"]]],
    ["Email form", [["emailCta", "Open form"], ["emailHide", "Hide form"], ["emailHeading", "Form heading"], ["emailIntro", "Intro paragraph"], ["emailFirstLabel", "First-name label"], ["emailEmailLabel", "Email label"], ["emailPhoneLabel", "Phone label"], ["emailPhoneHint", "Phone hint"], ["emailConsent", "Consent checkbox"], ["emailButton", "Submit button"]]],
    ["Email confirmation", [["emailConfirmTitle", "Title"], ["emailConfirmPrefix", "Before the address"], ["emailConfirmSuffix", "After the address"], ["emailConfirmFallback", "If no address"]]],
    ["Results page headings", [["resultsMatchLabel", "Match-chart heading"], ["scoreHowLabel", "“How the score works” heading"], ["scoreHowText", "“How the score works” copy"], ["profileEyebrow", "Profile eyebrow"], ["profileLabel", "“What we’re seeing” heading"]]],
    ["Results title (one per outcome)", [["titleLight", "Nothing much flagged"], ["titleFirstTimer", "First-ever detox → full kit"], ["titleFullKit", "Several areas → full kit"], ["titleTargetedOne", "One area stood out"], ["titleTargetedMany", "Two areas stood out"], ["titleDefault", "Fallback"]]],
    ["“What this means” paragraph", [["overviewLight", "Nothing much flagged"], ["overviewFirstTimer", "First-ever detox — uses {areas}"], ["overviewFullKit", "Several areas — uses {areas}"], ["overviewFullTail", "Closing line (both kit versions)"], ["overviewTargetedOne", "One area — uses {areas}"], ["overviewTargetedMany", "Two areas — uses {areas}"]]],
    ["Product headlines", [["whyLiver", "Liver Cleanse"], ["whyKidney", "Kidney Cleanse"], ["whyParasite", "Parasite Killer"], ["whyMetal", "Heavy Metal Detox"], ["recKit", "Full kit (top pick)"], ["recStartSmaller", "“Start smaller” option"], ["recBinder", "The Binder (paired)"], ["recKitUpsell", "Full kit (upgrade & save)"], ["recLightPrimary", "Gentle reset pick"], ["recLightBinder", "The Binder (gentle reset)"]]],
    ["Reason chips & area words", [["chipLightReset", "Gentle-reset chip"], ["chipBinderPairsLiver", "Binder chip (gentle reset)"], ["chipBinderPairs", "Binder chip — uses {products}"], ["chipKitSaves", "Kit savings chip — uses {amount}"], ["chipCoversAll", "Kit “covers all” chip"], ["chipUpsellSaves", "Upsell savings chip — uses {amount}"], ["areaWordLiver", "Area word: liver"], ["areaWordKidney", "Area word: kidneys"], ["areaWordParasite", "Area word: gut"], ["areaWordMetal", "Area word: metals"]]],
    ["Results & cart", [["topMatch", "Top-match badge"], ["recsLabel", "Recommendations heading"], ["addToCart", "Add to cart"], ["addedLabel", "Added state"], ["addAll", "Add all (prefix)"], ["addAllSuffix", "Add all (suffix)"], ["viewProduct", "Visit product"], ["cartCountSuffix", "Cart bar suffix"], ["checkout", "Checkout button"], ["startOver", "Start over"]]],
  ];
  return (
    <CanvasPad>
      <ScreenLabel>Buttons & email copy</ScreenLabel>
      <div style={{
        border: "1px solid " + t.border, borderRadius: 10, padding: 11, marginBottom: 14, maxWidth: 620, minWidth: 0,
        background: t.surface, fontFamily: FIGTREE, fontSize: 12, color: t.fg2, lineHeight: 1.5,
      }}>
        <b style={{ color: t.fg }}>These are live.</b> Publishing sends this copy to the storefront, which
        renders it in place of its own defaults. The results title, the “What this means” paragraph and the
        product headlines also go into the results email word-for-word — edit them once here and both the
        page and the email change. Anything in <code>{"{braces}"}</code> is filled in per shopper (their
        areas, the live kit saving); leave the token in the sentence or it stops personalizing.
        <span style={{ display: "block", marginTop: 6, color: t.faint }}>Tap any line to expand it and edit the full text.</span>
      </div>
      <div style={{ display: "grid", gap: 18, maxWidth: 620, minWidth: 0 }}>
        {groups.map(([title, fields], si) => (
          <div key={title} style={{ minWidth: 0 }}>
            <div style={{ fontFamily: OSWALD, fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: t.fg, padding: "0 2px 8px" }}>{title}</div>
            <div style={{ display: "grid", gap: 8, minWidth: 0 }}>
              {fields.map(([k, label]) => {
                const id = si + "-" + k;
                return (
                  <CopyField key={k} label={label} value={ui[k]} onChange={set(k)}
                    open={openField === id}
                    onToggle={() => setOpenField((cur) => (cur === id ? null : id))} />
                );
              })}
            </div>
          </div>
        ))}
        <div style={{ border: "1px solid " + t.border, borderRadius: 12, padding: 12, background: t.surface }}>
          <div style={{ fontFamily: OSWALD, fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: t.fg, marginBottom: 6 }}>Confirmation preview</div>
          <div style={{ border: "1px solid " + t.border, borderRadius: 12, padding: 14, background: "#dcfce7", color: "#166534" }}>
            <div style={{ fontFamily: OSWALD, fontSize: 16, fontWeight: 700 }}>{ui.emailConfirmTitle || "On its way!"} ✓</div>
            {/* The storefront splits this sentence around the shopper's address, so
                preview it the same way — otherwise the spacing reads wrong here. */}
            <div style={{ fontFamily: FIGTREE, fontSize: 12.5, marginTop: 3 }}>
              {ui.emailConfirmPrefix || ""}
              <b>you@example.com</b>
              {ui.emailConfirmSuffix || ""}
            </div>
          </div>
        </div>
      </div>
    </CanvasPad>
  );
}

// ============================================================================
// INSPECTOR
// ============================================================================
function Inspector({ doc, touch, sel, stack }: any) {
  const t = useT();
  let body: React.ReactNode = null;

  if (sel.kind === "intro") body = <IntroInspector doc={doc} touch={touch} idx={sel.idx} />;
  else if (sel.kind === "step") {
    const step = (doc.flow || [])[sel.idx];
    if (step?.type === "info") body = <InfoInspector doc={doc} touch={touch} k={step.key} />;
    else if (step) body = <QuestionInspector doc={doc} touch={touch} qid={step.qid} />;
  } else if (sel.kind === "results" || sel.kind === "ui") body = <SettingsInspector doc={doc} touch={touch} />;
  else if (sel.kind === "products") body = <ProductsInspector />;

  return (
    <Card pad={13} style={{ position: stack ? "static" : "sticky", top: 12, minWidth: 0 }}>
      <div style={{ fontFamily: OSWALD, fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: t.fg, marginBottom: 10 }}>
        Page settings
      </div>
      {body || <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint }}>Nothing to configure.</div>}
    </Card>
  );
}

function InsSection({ title, help, children }: any) {
  const t = useT();
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: FIGTREE, fontSize: 10.5, fontWeight: 800, color: t.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>{title}</div>
      {help && <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, lineHeight: 1.45, marginBottom: 7 }}>{help}</div>}
      {children}
    </div>
  );
}

const insInput = (t: any): React.CSSProperties => ({
  fontFamily: FIGTREE, fontSize: 12.5, padding: "6px 8px", borderRadius: 7,
  border: "1px solid " + t.border, background: t.surface2, color: t.fg, width: "100%", boxSizing: "border-box",
});

function ImagePicker({ value, onChange }: any) {
  const t = useT();
  return (
    <InsSection title="Illustration" help="Choose which of the four quiz illustrations this page uses. The artwork itself lives in the Shopify theme — swapping the picture is a Shopify job, choosing which page shows it is this one.">
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} style={insInput(t)}>
        <option value="">— none —</option>
        {IMAGE_SLOTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
    </InsSection>
  );
}

function IntroInspector({ doc, touch, idx }: any) {
  const iv = (doc.intros || [])[idx];
  if (!iv) return null;
  return (
    <>
      <InsSection title="A/B variant" help="One of these is shown at random on load. Completion rate is compared per variant on the Analytics tab.">
        <Badge tone="neutral">Variant {String.fromCharCode(65 + idx)}</Badge>
      </InsSection>
      {/* The intro hero is a fixed art island in the theme (ART.homeHero) — the same
          image for all three variants. Deliberately not a picker: an admin-side value
          here would be silently ignored by the storefront. */}
      <InsSection title="Hero image" help="All three intro variants share one hero image, set in Shopify → Theme editor on the quiz section. It isn't per-variant.">
        <Badge tone="neutral">Theme-managed</Badge>
      </InsSection>
    </>
  );
}

function InfoInspector({ doc, touch, k }: any) {
  const t = useT();
  const info = (doc.info || {})[k];
  if (!info) return null;
  return (
    <>
      <InsSection title="Page id" help="Used by analytics (info_viewed). Keep it stable once live.">
        <code style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2 }}>{k}</code>
      </InsSection>
      <ImagePicker value={info.illo} onChange={(v: any) => touch((d: any) => { d.info[k].illo = v; })} />
      <InsSection title="Colors">
        <div style={{ display: "grid", gap: 7 }}>
          <ColorRow label="Background" value={info.bg} onChange={(v: any) => touch((d: any) => { d.info[k].bg = v; })} />
          <ColorRow label="Text" value={info.ink} onChange={(v: any) => touch((d: any) => { d.info[k].ink = v; })} />
        </div>
      </InsSection>
    </>
  );
}

function ColorRow({ label, value, onChange }: any) {
  const t = useT();
  return (
    <label style={{ display: "grid", gridTemplateColumns: "1fr 30px", gap: 7, alignItems: "center" }}>
      <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2 }}>{label}</span>
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
        style={{ width: 30, height: 24, padding: 0, border: "1px solid " + t.border, borderRadius: 6, background: "none", cursor: "pointer" }} />
    </label>
  );
}

function QuestionInspector({ doc, touch, qid }: any) {
  const t = useT();
  const qi = (doc.questions || []).findIndex((q: any) => q.id === qid);
  const q = (doc.questions || [])[qi];
  if (!q) return null;

  return (
    <>
      <InsSection title="Question id" help="This is the analytics key. Renaming it after go-live splits your drop-off reporting.">
        <input value={q.id} onChange={(e) => {
          const next = e.target.value.trim();
          touch((d: any) => {
            const old = d.questions[qi].id;
            d.questions[qi].id = next;
            (d.flow || []).forEach((s: any) => { if (s.type === "q" && s.qid === old) s.qid = next; });
          });
        }} style={insInput(t)} />
      </InsSection>

      <InsSection title="Answer type">
        <label style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2 }}>
          <input type="checkbox" checked={!!q.multi} onChange={(e) => touch((d: any) => { d.questions[qi].multi = e.target.checked; })} />
          Allow multiple answers
        </label>
        <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 5, lineHeight: 1.45 }}>
          Single-select advances automatically on tap. Multi-select shows a Continue button.
        </div>
      </InsSection>

      <InsSection title="Left panel theme" help="Sets the color of the illustration panel beside this question.">
        <select value={q.theme || "general"} onChange={(e) => touch((d: any) => { d.questions[qi].theme = e.target.value; })} style={insInput(t)}>
          {Object.entries(doc.themes || {}).map(([k, v]: any) => <option key={k} value={k}>{v.label || k}</option>)}
        </select>
      </InsSection>

      <ImagePicker value={q.image} onChange={(v: any) => touch((d: any) => { d.questions[qi].image = v; })} />

      <InsSection
        title="Scoring"
        help={`Each answer adds points to one or more detox areas. Points decide the winning area and the ranked products. ${doc.settings?.scoreCap ?? 8} points reads as a 100% match. Leave an answer at zero if it shouldn't count.`}
      >
        <div style={{ display: "grid", gap: 9 }}>
          {(q.options || []).map((o: any, oi: number) => (
            <OptionScore key={oi} doc={doc} touch={touch} qi={qi} oi={oi} o={o} />
          ))}
          {!(q.options || []).length && <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>Add answers on the page first.</div>}
        </div>
      </InsSection>
    </>
  );
}

function OptionScore({ doc, touch, qi, oi, o }: any) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const setScore = (a: string, n: number) => touch((d: any) => {
    const opt = d.questions[qi].options[oi];
    opt.score = opt.score || {};
    if (n > 0) opt.score[a] = n;
    else { delete opt.score[a]; delete (opt.reasons || {})[a]; }
  });
  return (
    <div style={{ border: "1px solid " + t.border, borderRadius: 10, padding: 9, background: t.surface2 }}>
      <div style={{ fontFamily: FIGTREE, fontSize: 12, fontWeight: 700, color: t.fg, marginBottom: 7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {o.label || <span style={{ color: t.faint, fontWeight: 400 }}>Untitled answer</span>}
      </div>
      <div style={{ display: "grid", gap: 5 }}>
        {AREAS.map((a) => (
          <div key={a} style={{ display: "grid", gridTemplateColumns: "1fr 76px", gap: 7, alignItems: "center" }}>
            <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.fg2 }}>{AREA_LABEL[a]}</span>
            <Stepper value={Number(o.score?.[a]) || 0} onChange={(n: number) => setScore(a, n)} />
          </div>
        ))}
      </div>

      <button onClick={() => setOpen(!open)} style={{
        marginTop: 7, background: "none", border: "none", cursor: "pointer", padding: 0,
        fontFamily: FIGTREE, fontSize: 11, fontWeight: 700, color: t.accent,
      }}>{open ? "Hide" : "More"} — reason chips, exclusive, kit</button>

      {open && (
        <div style={{ marginTop: 8, display: "grid", gap: 7 }}>
          <div style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.faint, lineHeight: 1.45 }}>
            Reason chips show on the results page as “why we recommended this”. Write each as a fragment that
            finishes “…because of <i>your answer</i>”. One per scored area.
          </div>
          {AREAS.filter((a) => Number(o.score?.[a]) > 0).map((a) => (
            <label key={a} style={{ display: "grid", gap: 3 }}>
              <span style={{ fontFamily: FIGTREE, fontSize: 10.5, fontWeight: 700, color: t.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{AREA_LABEL[a]} reason</span>
              <input value={o.reasons?.[a] ?? ""} placeholder="e.g. low daily water intake"
                onChange={(e) => touch((d: any) => {
                  const opt = d.questions[qi].options[oi];
                  opt.reasons = opt.reasons || {};
                  if (e.target.value) opt.reasons[a] = e.target.value; else delete opt.reasons[a];
                })} style={insInput(t)} />
            </label>
          ))}
          {!AREAS.some((a) => Number(o.score?.[a]) > 0) && (
            <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>Score an area above to add its reason chip.</div>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: FIGTREE, fontSize: 12, color: t.fg2 }}>
            <input type="checkbox" checked={!!o.exclusive} onChange={(e) => touch((d: any) => { d.questions[qi].options[oi].exclusive = e.target.checked || undefined; })} />
            “None of these” — clears the other answers
          </label>

          <label style={{ display: "grid", gridTemplateColumns: "1fr 76px", gap: 7, alignItems: "center" }}>
            <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.fg2 }}>4-Stage Kit weight</span>
            <Stepper value={Number(o.kit) || 0} onChange={(n: number) => touch((d: any) => { d.questions[qi].options[oi].kit = n || undefined; })} />
          </label>
          <div style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.faint, lineHeight: 1.45 }}>
            Pushes the 4-Stage Kit toward the top recommendation. Used on “never detoxed before” (3) and
            “once or twice” (1).
          </div>
        </div>
      )}
    </div>
  );
}

function Stepper({ value, onChange }: any) {
  const t = useT();
  const btn: React.CSSProperties = {
    width: 22, height: 22, borderRadius: 6, border: "1px solid " + t.border, background: t.surface,
    color: t.fg2, cursor: "pointer", fontFamily: FIGTREE, fontSize: 13, lineHeight: 1, display: "grid", placeItems: "center",
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
      <button style={btn} onClick={() => onChange(Math.max(0, value - 1))}>−</button>
      <span style={{
        fontFamily: FIGTREE, fontSize: 12.5, fontWeight: 700, minWidth: 14, textAlign: "center",
        color: value > 0 ? t.accent : t.faint,
      }}>{value}</span>
      <button style={btn} onClick={() => onChange(value + 1)}>+</button>
    </div>
  );
}

function SettingsInspector({ doc, touch }: any) {
  const t = useT();
  const s = doc.settings || {};
  const set = (k: string) => (v: any) => touch((d: any) => { (d.settings = d.settings || {})[k] = v; });
  return (
    <>
      {/* The theme's applyRemoteConfig() reads questions/flow/intros/info/themes/products
          but NOT `settings` — these live as section settings in the theme editor until it
          does. Saying so beats a control that silently does nothing. */}
      <div style={{
        border: "1px solid " + t.border, borderRadius: 9, padding: 9, marginBottom: 12,
        background: t.surface2, fontFamily: FIGTREE, fontSize: 11, color: t.muted, lineHeight: 1.45,
      }}>
        <b style={{ color: t.fg2 }}>Not live yet.</b> The storefront currently reads these from the
        theme editor, not from here. Publishing changes them in the config but won’t change the quiz
        until the theme is updated to read <code>settings</code>.
      </div>
      <InsSection title="Score cap" help="Raw points that read as a 100% match on the results chart.">
        <input type="number" value={s.scoreCap ?? 8} onChange={(e) => set("scoreCap")(Number(e.target.value) || 0)} style={insInput(t)} />
      </InsSection>
      <InsSection title="Incentive" help="Shows an offer banner on the results page.">
        <select value={s.incentiveMode || "none"} onChange={(e) => set("incentiveMode")(e.target.value)} style={{ ...insInput(t), marginBottom: 7 }}>
          <option value="none">No incentive</option>
          <option value="percent">Percent off</option>
          <option value="shipping">Free shipping</option>
          <option value="both">Both</option>
        </select>
        {(s.incentiveMode === "percent" || s.incentiveMode === "both") && (
          <div style={{ display: "grid", gap: 7 }}>
            <input type="number" value={s.incentivePercent ?? 10} onChange={(e) => set("incentivePercent")(Number(e.target.value) || 0)} placeholder="Percent" style={insInput(t)} />
            <input value={s.incentiveCode ?? ""} onChange={(e) => set("incentiveCode")(e.target.value)} placeholder="Discount code" style={insInput(t)} />
          </div>
        )}
      </InsSection>
      <InsSection title="Force intro variant" help="Pins one A/B variant. Use for QA, then set back to Auto.">
        <select value={String(s.forceIntro ?? "auto")} onChange={(e) => set("forceIntro")(e.target.value)} style={insInput(t)}>
          <option value="auto">Auto (random A/B)</option>
          {(doc.intros || []).map((_: any, i: number) => <option key={i} value={String(i)}>Variant {String.fromCharCode(65 + i)}</option>)}
        </select>
      </InsSection>
    </>
  );
}

function ProductsInspector() {
  const t = useT();
  return (
    <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, lineHeight: 1.5 }}>
      Product names, prices and discounts come from Shopify at render time. To change them, edit the product
      in Shopify — not here.
    </div>
  );
}

// ============================================================================
function uniqueId(base: string, taken: string[]): string {
  let id = base, n = 2;
  while (taken.includes(id)) id = `${base}-${n++}`;
  return id;
}
